 
/**
 * Invoice Service
 *
 * API client for invoice management (B2B and Export invoices).
 */

import {
  get,
  post,
  patch,
  buildQueryString,
  type ApiResponse,
} from './api-client';

import type {
  Invoice,
  InvoiceListResponse,
  InvoiceListFilters,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  EditPostedInvoiceRequest,
  VoidInvoiceRequest,
  InvoiceType,
  InvoiceVersion,
  InvoiceVersionsResponse,
} from '../types/billing';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert frontend invoice type to proto enum value.
 */
function toProtoInvoiceType(invoiceType: InvoiceType): string {
  const mapping: Record<InvoiceType, string> = {
    'b2b': 'INVOICE_TYPE_B2B',
    'b2c': 'INVOICE_TYPE_B2C',
    'export': 'INVOICE_TYPE_EXPORT',
    'credit_note': 'INVOICE_TYPE_CREDIT_NOTE',
    'debit_note': 'INVOICE_TYPE_DEBIT_NOTE',
    'vendor_bill': 'INVOICE_TYPE_VENDOR_BILL',
    'vendor_credit_note': 'INVOICE_TYPE_VENDOR_CREDIT_NOTE',
  };
  return mapping[invoiceType] ?? 'INVOICE_TYPE_UNSPECIFIED';
}

/**
 * Convert proto enum format to frontend invoice type.
 */
function fromProtoInvoiceType(protoType: string): InvoiceType {
  const mapping: Record<string, InvoiceType> = {
    'INVOICE_TYPE_B2B': 'b2b',
    'INVOICE_TYPE_B2C': 'b2c',
    'INVOICE_TYPE_EXPORT': 'export',
    'INVOICE_TYPE_CREDIT_NOTE': 'credit_note',
    'INVOICE_TYPE_DEBIT_NOTE': 'debit_note',
    'INVOICE_TYPE_VENDOR_BILL': 'vendor_bill',
    'INVOICE_TYPE_VENDOR_CREDIT_NOTE': 'vendor_credit_note',
  };
  return mapping[protoType] ?? 'b2b';
}

/**
 * Convert proto enum format to frontend invoice status.
 */
function fromProtoInvoiceStatus(protoStatus: string): string {
  const mapping: Record<string, string> = {
    'INVOICE_STATUS_DRAFT': 'draft',
    'INVOICE_STATUS_POSTED': 'posted',
    'INVOICE_STATUS_PAID': 'paid',
    'INVOICE_STATUS_PARTIAL': 'partial',
    'INVOICE_STATUS_VOIDED': 'voided',
    'INVOICE_STATUS_CANCELLED': 'cancelled',
  };
  return mapping[protoStatus] ?? 'draft';
}

/**
 * Transform invoice response from proto format to frontend format.
 */
function transformInvoiceResponse<T extends { data?: Invoice | null }>(response: T): T {
  if (response.data) {
    if (response.data.invoiceType) {
      response.data.invoiceType = fromProtoInvoiceType(response.data.invoiceType);
    }
    if (response.data.status) {
      response.data.status = fromProtoInvoiceStatus(response.data.status) as Invoice['status'];
    }
  }
  return response;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * List invoices for a ledger.
 */
export async function listInvoices(
  orgId: string,
  ledgerId: string,
  filters?: InvoiceListFilters,
  pagination?: { pageSize?: number; pageToken?: string; page?: number }
): Promise<ApiResponse<InvoiceListResponse>> {
  const { pageSize, pageToken, page } = pagination ?? {}
  const query = buildQueryString({
    pageSize,
    pageToken,
    page,
    invoiceType: filters?.invoiceType,
    status: filters?.status,
    dueStatus: filters?.dueStatus,
    partyId: filters?.partyId,
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    search: filters?.search,
    sortField: filters?.sortField,
    sortDir: filters?.sortDir,
    paymentStatus: filters?.paymentStatus,
    currency: filters?.currency,
  });
  const response = await get<InvoiceListResponse>(
    `/organizations/${orgId}/ledgers/${ledgerId}/invoices${query}`
  );

  // Transform proto format to frontend format for all invoices
  if (response.data?.invoices) {
    response.data.invoices = response.data.invoices.map((invoice: Invoice) => ({
      ...invoice,
      invoiceType: invoice.invoiceType ? fromProtoInvoiceType(invoice.invoiceType as string) : invoice.invoiceType,
      status: invoice.status ? fromProtoInvoiceStatus(invoice.status as string) as Invoice['status'] : invoice.status,
    }));
  }

  return response;
}

/**
 * List invoices for a party across all ledgers.
 */
export async function listInvoicesByParty(
  orgId: string,
  partyId: string,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<InvoiceListResponse>> {
  const query = buildQueryString({ pageSize, pageToken });
  const response = await get<InvoiceListResponse>(
    `/organizations/${orgId}/parties/${partyId}/invoices${query}`
  );

  if (response.data?.invoices) {
    response.data.invoices = response.data.invoices.map((invoice: Invoice) => ({
      ...invoice,
      invoiceType: invoice.invoiceType ? fromProtoInvoiceType(invoice.invoiceType as string) : invoice.invoiceType,
      status: invoice.status ? fromProtoInvoiceStatus(invoice.status as string) as Invoice['status'] : invoice.status,
    }));
  }

  return response;
}

/**
 * Get invoice by ID with items.
 */
export async function getInvoice(
  orgId: string,
  invoiceId: string
): Promise<ApiResponse<Invoice>> {
  const response = await get<Invoice>(`/organizations/${orgId}/invoices/${invoiceId}`);
  return transformInvoiceResponse(response);
}

export interface BatchInvoicesResult {
  invoices: Record<string, Invoice>;
}

/**
 * Get full invoices (with items) for many IDs in a single request, instead
 * of one getInvoice call per invoice.
 */
export async function getInvoicesBatch(
  orgId: string,
  invoiceIds: string[]
): Promise<ApiResponse<BatchInvoicesResult>> {
  const response = await post<BatchInvoicesResult>(`/organizations/${orgId}/invoices/batch`, { invoiceIds });
  if (response.data) {
    for (const invoice of Object.values(response.data.invoices)) {
      if (invoice.invoiceType) { invoice.invoiceType = fromProtoInvoiceType(invoice.invoiceType); }
      if (invoice.status) { invoice.status = fromProtoInvoiceStatus(invoice.status) as Invoice['status']; }
    }
  }
  return response;
}

/**
 * Create a new invoice.
 */
export async function createInvoice(
  orgId: string,
  ledgerId: string,
  data: CreateInvoiceRequest
): Promise<ApiResponse<Invoice>> {
  // Convert invoice type to proto enum
  const requestData = {
    ...data,
    invoiceType: toProtoInvoiceType(data.invoiceType),
  };

  const response = await post<Invoice>(
    `/organizations/${orgId}/ledgers/${ledgerId}/invoices`,
    requestData
  );
  return transformInvoiceResponse(response);
}

/**
 * Update a draft invoice.
 */
export async function updateInvoice(
  orgId: string,
  invoiceId: string,
  data: UpdateInvoiceRequest
): Promise<ApiResponse<Invoice>> {
  const response = await patch<Invoice>(
    `/organizations/${orgId}/invoices/${invoiceId}`,
    data
  );
  return transformInvoiceResponse(response);
}

/**
 * Post an invoice (finalize it and create journal entry).
 */
export async function postInvoice(
  orgId: string,
  invoiceId: string,
  invoiceDate?: string
): Promise<ApiResponse<Invoice>> {
  const response = await post<Invoice>(
    `/organizations/${orgId}/invoices/${invoiceId}/post`,
    invoiceDate ? { invoiceDate } : undefined
  );
  return transformInvoiceResponse(response);
}

/**
 * Void a posted invoice.
 */
export async function voidInvoice(
  orgId: string,
  invoiceId: string,
  reason: string
): Promise<ApiResponse<Invoice>> {
  const data: VoidInvoiceRequest = { reason };
  const response = await post<Invoice>(
    `/organizations/${orgId}/invoices/${invoiceId}/void`,
    data
  );
  return transformInvoiceResponse(response);
}

/**
 * Edit a posted invoice (voids old journal entry, creates new one).
 * Party details are immutable on posted invoices.
 */
export async function editPostedInvoice(
  orgId: string,
  invoiceId: string,
  data: EditPostedInvoiceRequest
): Promise<ApiResponse<Invoice>> {
  const response = await patch<Invoice>(
    `/organizations/${orgId}/invoices/${invoiceId}/edit-posted`,
    data
  );
  return transformInvoiceResponse(response);
}

/**
 * Generate PDF for an invoice.
 * Returns the PDF as a blob for download.
 */
export async function generateInvoicePDF(
  orgId: string,
  invoiceId: string
): Promise<Blob | null> {
  try {
    // Note: Using port 8081 for backend API
    /* v8 ignore next */
    const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8081';
    const response = await fetch(
      `${baseUrl}/organizations/${orgId}/invoices/${invoiceId}/pdf`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      return null;
    }

    // Get the JSON response with base64-encoded PDF
    const data = await response.json() as { pdfContent: string };

    // Decode base64 to binary
    const binaryString = window.atob(data.pdfContent);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob from binary data
    return new Blob([bytes], { type: 'application/pdf' });
  } catch (_error) {
    return null;
  }
}

/**
 * Download invoice PDF.
 * Generates the PDF and triggers a browser download.
 */
export async function downloadInvoicePDF(
  orgId: string,
  invoiceId: string,
  invoiceNumber: string
): Promise<boolean> {
  const blob = await generateInvoicePDF(orgId, invoiceId);
  if (!blob) {return false;}

  // Create a download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);

  return true;
}

// ============================================================================
// Invoice Version History
// ============================================================================

/**
 * Get all version history for an invoice.
 *
 * @param orgId - Organization ID
 * @param invoiceId - Invoice ID
 * @returns List of all invoice versions
 */
export async function getInvoiceVersions(
  orgId: string,
  invoiceId: string
): Promise<ApiResponse<InvoiceVersionsResponse>> {
  return get<InvoiceVersionsResponse>(
    `/organizations/${orgId}/invoices/${invoiceId}/versions`
  );
}

/**
 * Get a specific version of an invoice.
 *
 * @param orgId - Organization ID
 * @param invoiceId - Invoice ID
 * @param versionNumber - Version number to retrieve
 * @returns Specific invoice version
 */
export async function getInvoiceVersion(
  orgId: string,
  invoiceId: string,
  versionNumber: number
): Promise<ApiResponse<{ version: InvoiceVersion }>> {
  return get<{ version: InvoiceVersion }>(
    `/organizations/${orgId}/invoices/${invoiceId}/versions/${versionNumber}`
  );
}
